/**
 * Mobile / native app detection (sync, safe on web and Capacitor).
 */
export function isNativeAppShell() {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.documentElement.classList.contains('capacitor-native');
}

export function getNativePlatform() {
  if (typeof document === 'undefined') {
    return 'web';
  }
  const htmlClasses = document.documentElement.classList;
  const bodyClasses = document.body.classList;
  if (htmlClasses.contains('capacitor-ios') || bodyClasses.contains('capacitor-ios')) return 'ios';
  if (htmlClasses.contains('capacitor-android') || bodyClasses.contains('capacitor-android')) return 'android';
  if (htmlClasses.contains('capacitor-native') || bodyClasses.contains('capacitor-native')) {
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  }
  return 'web';
}

const TABLET_MIN_SHORT_SIDE = 600;
const TABLET_MIN_LONG_SIDE = 900;

/**
 * iPad / Android tablet form factor inside the Capacitor native app.
 * Used for spacing/safe-area tweaks within the mobile app shell — not for choosing desktop chrome.
 */
export function isNativeTabletShell() {
  if (!isNativeAppShell() || typeof window === 'undefined') {
    return false;
  }

  const w = window.innerWidth;
  const h = window.innerHeight;
  const minSide = Math.min(w, h);
  const maxSide = Math.max(w, h);
  if (minSide < TABLET_MIN_SHORT_SIDE || maxSide < TABLET_MIN_LONG_SIDE) {
    return false;
  }

  const platform = getNativePlatform();
  if (platform === 'android') {
    return true;
  }

  const ua = navigator.userAgent || '';
  if (/iPad/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ may report as Mac with touch
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

/** Tablet/iPad form factor (native app or Safari on iPad) at md+ viewport. */
export function isTabletFormFactor() {
  if (typeof window === 'undefined') {
    return false;
  }

  const w = window.innerWidth;
  const h = window.innerHeight;
  const minSide = Math.min(w, h);
  const maxSide = Math.max(w, h);
  if (minSide < TABLET_MIN_SHORT_SIDE || maxSide < TABLET_MIN_LONG_SIDE) {
    return false;
  }

  if (isNativeTabletShell()) {
    return true;
  }

  const ua = navigator.userAgent || '';
  if (/iPad/i.test(ua)) {
    return true;
  }
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  // Android tablet (no "Mobile" in UA on many tablets)
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) {
    return true;
  }

  return false;
}

/** Toggle `capacitor-tablet` on <html> for CSS hooks (safe areas, spacing). */
export function syncNativeTabletShellClass() {
  if (typeof document === 'undefined') {
    return;
  }
  const isTablet = isNativeTabletShell();
  document.documentElement.classList.toggle('capacitor-tablet', isTablet);
}

/** Android WebView often lacks reliable `dvh`; use `vh` on native Android. */
export function mobileViewportUnit() {
  return getNativePlatform() === 'android' ? 'vh' : 'dvh';
}

export function getMobileShellViewportSx() {
  const unit = mobileViewportUnit();
  return {
    minHeight: `100${unit}`,
    height: `100${unit}`,
    maxHeight: `100${unit}`,
  };
}

/** Keep MUI portaled overlays inside the WebView on Capacitor (fixes broken bottom sheets). */
export function getNativeDrawerPortalProps() {
  if (!isNativeAppShell()) {
    return {};
  }
  return { disablePortal: true };
}

export const MOBILE_LAYOUT = {
  touchMinHeight: 44,
  pagePaddingX: { xs: 1.5, sm: 2, md: 2.5 },
  pagePaddingY: { xs: 1.5, sm: 2 },
  headerOffsetNative: 122,
  mobileAppHeaderHeight: 104,
  mobileBottomNavHeight: 68,
  mobileFabLift: 14,
  /** Bottom inset for scrollable main content (nav + FAB overlap + buffer). */
  mobileMainBottomInset:
    'calc(68px + env(safe-area-inset-bottom) + 22px)',
};
